import { Power, PowerOff, Trash2, Cog } from "lucide-react";
import type { SimOverride, SimProfile } from "@opus-dt/shared-types";
import { SlideOver } from "@/components/ui/slide-over";
import { Button } from "@/components/ui/button";
import { Pill } from "@/components/ui/pill";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import type { SandboxMachine, SandboxStage } from "@/types/sim-sandbox.types";
import { useSandboxDraft } from "./hooks/useSandboxDraft";
import { SimParamFields } from "./SimParamFields";
import { ParamOriginAlert } from "./ParamOriginAlert";

interface Props {
  machine: SandboxMachine;
  stages: SandboxStage[];
  /** Erro do preview do servidor: mostra estado de erro no lugar dos avisos. */
  previewError: unknown;
  onRetryPreview: () => void;
}

// Sheet lateral da máquina do cenário (SlideOver padrão). 3 abas:
// Parâmetros (5 params + origem), Dados básicos (read-only), Compatibilidade; move de estágio;
// ações Desligar/Religar/Remover. Fecha ao deselecionar (pane click) ou ao remover.
// Aviso de origem (estrutural/transitório/erro) no topo da aba Parâmetros.
export function MachineParamsSheet({ machine, stages, previewError, onRetryPreview }: Props) {
  const setMachineSim = useSandboxDraft((s) => s.setMachineSim);
  const togglePresent = useSandboxDraft((s) => s.togglePresent);
  const removeMachine = useSandboxDraft((s) => s.removeMachine);
  const setStage = useSandboxDraft((s) => s.setStage);
  const select = useSandboxDraft((s) => s.select);

  const onChange = (patch: { simProfile?: SimProfile; simOverrides?: SimOverride[] }) =>
    setMachineSim(machine.key, patch);
  const close = () => select(null);
  const orderedStages = [...stages].sort((a, b) => a.order - b.order);

  return (
    <SlideOver
      open
      onOpenChange={(o) => (o ? undefined : close())}
      title={machine.name}
      width={440}
    >
      <div className="space-y-4 p-4">
        <div className="flex min-w-0 items-center gap-2">
          <Cog className="h-4 w-4 shrink-0 text-text-tertiary" />
          <span className="truncate font-mono text-[11px] text-text-tertiary">{machine.code}</span>
          <Pill tone={machine.present ? "success" : "neutral"} withDot className="ml-1">
            {machine.present ? "Ativa" : "Desligada"}
          </Pill>
          {machine.origin === "acervo" ? (
            <span className="text-[9px] font-semibold uppercase text-primary">Acervo</span>
          ) : null}
        </div>

        <div className="space-y-1">
          <Label htmlFor="sbx-stage" className="text-xs">
            Estágio
          </Label>
          <Select
            id="sbx-stage"
            value={machine.stageKey}
            onChange={(e) => setStage(machine.key, e.target.value)}
          >
            {orderedStages.map((s) => (
              <option key={s.key} value={s.key}>
                {s.name}
              </option>
            ))}
          </Select>
        </div>

        <Tabs defaultValue="params">
          <TabsList aria-label="Configuração da máquina">
            <TabsTrigger value="params">Parâmetros</TabsTrigger>
            <TabsTrigger value="basics">Dados básicos</TabsTrigger>
            <TabsTrigger value="compat">Compatibilidade</TabsTrigger>
          </TabsList>

          <TabsContent value="params" className="space-y-3 pt-3">
            <ParamOriginAlert
              machine={machine}
              serverError={previewError}
              onRetry={onRetryPreview}
            />
            <SimParamFields machine={machine} onChange={onChange} />
          </TabsContent>

          <TabsContent value="basics" className="space-y-2 pt-3 text-sm">
            <Row label="Código" value={machine.code} />
            <Row label="Nome" value={machine.name} />
            <Row label="Origem" value={machine.origin === "acervo" ? "Acervo" : "Linha base"} />
            <p className="text-[11px] text-muted-foreground">
              Dados de cadastro (somente leitura). Edite os parâmetros na aba Parâmetros.
            </p>
          </TabsContent>

          <TabsContent value="compat" className="space-y-2 pt-3 text-sm">
            <p className="text-muted-foreground">
              A compatibilidade produto×máquina é validada ao rodar a simulação: máquinas
              incompatíveis bloqueiam a rodada com aviso.
            </p>
          </TabsContent>
        </Tabs>

        <div className="flex items-center gap-2 border-t pt-3">
          <Button variant="outline" size="sm" onClick={() => togglePresent(machine.key)}>
            {machine.present ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
            {machine.present ? "Desligar" : "Religar"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive"
            onClick={() => {
              removeMachine(machine.key);
              close();
            }}
          >
            <Trash2 className="h-4 w-4" />
            Remover
          </Button>
        </div>
      </div>
    </SlideOver>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
